import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ExtendedListenableSubscribable } from '@benzinga/subscribable';

import { StockSymbol } from '@benzinga/session';
import {
  ChartDataResponse,
  ChartRange,
  CryptoChartMultiply,
  CryptoChartTime,
  CryptoChartResponse,
  ChartOptions,
  Candle,
} from './entities';
import { BarsRestful, ChartRestful } from './restful';
import { Session } from '@benzinga/session';

interface ErrorEvent {
  error: SafeError;
  errorType: 'get_chart_error' | 'get_crypto_chart_error';
  type: 'error';
}

interface ChartGetEvent {
  chart: ChartDataResponse;
  type: 'chart_get';
}

interface CryptoChartGetEvent {
  chart: CryptoChartResponse;
  type: 'crypto_chart_get';
}

interface BarsGetEvent {
  bars: ChartDataResponse | ChartDataResponse[];
  type: 'bars_get';
}

export type ChartRestfulEvent = ErrorEvent | ChartGetEvent | CryptoChartGetEvent | BarsGetEvent;

interface ChartFunctions {
  getChart: ChartRequest['getChart'];
}

function chartRangeToString(range: ChartRange): string;
function chartRangeToString(range: undefined): undefined;
function chartRangeToString(range: ChartRange | undefined): string | undefined {
  if (range === undefined || typeof range === 'string') {
    return range as string | undefined;
  } else {
    return range.toISOString();
  }
}

export class ChartRequest extends ExtendedListenableSubscribable<ChartRestfulEvent, ChartFunctions> {
  private restful: ChartRestful;
  private barsRestful: BarsRestful;

  constructor(session: Session) {
    super();
    this.restful = new ChartRestful(session);
    this.barsRestful = new BarsRestful(session);
  }

  public getChart = async (options: ChartOptions): SafePromise<ChartDataResponse> => {
    const chart = await this.restful.getChart({
      apiKey: options.apiKey,
      extendedSession: options.extendedSession === false ? 'REGULAR' : options.extendedSession ? 'ANY' : undefined,
      from: chartRangeToString(options.from as ChartRange),
      interval: options.interval,
      symbol: options.symbol,
      to: chartRangeToString(options.to as ChartRange),
    });

    if (chart.err) {
      // No need to dispatch an error event, it's already handled in the manager.
    } else {
      this.dispatch({
        chart: chart.ok,
        type: 'chart_get',
      });
    }

    return chart;
  };

  public getCryptoChart = async (
    symbol: StockSymbol,
    from: string,
    to: string,
    multiply: CryptoChartMultiply,
    time: CryptoChartTime,
  ): SafePromise<CryptoChartResponse> => {
    const chart = await this.restful.getCryptoChart(symbol, from, to, multiply, time);
    if (chart.err) {
      this.dispatch({
        error: chart.err,
        errorType: 'get_crypto_chart_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        chart: chart.ok,
        type: 'crypto_chart_get',
      });
    }
    return chart;
  };

  public getBars = async (
    symbol: StockSymbol,
    params: { to?: string; from?: string; interval?: string },
  ): SafePromise<ChartDataResponse> => {
    const response = await this.barsRestful.getBars(symbol, params);
    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'get_chart_error',
        type: 'error',
      });
      return { err: response.err };
    } else {
      this.dispatch({
        bars: response.ok[0],
        type: 'bars_get',
      });
      return { ok: response.ok[0] };
    }
  };

  public getAllBars = async (
    symbols: StockSymbol[],
    params: { to?: string; from?: string; interval?: string },
  ): SafePromise<ChartDataResponse[]> => {
    const response = await this.barsRestful.getBars(symbols.join(','), params);
    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'get_chart_error',
        type: 'error',
      });
      return { err: response.err };
    } else {
      this.dispatch({
        bars: response.ok,
        type: 'bars_get',
      });
      return { ok: response.ok };
    }
  };

  public getEmbedChart = async (symbol: StockSymbol, timeframe: ChartRange): SafePromise<{ data: Candle[] }> => {
    const chart = await this.restful.getEmbedChart(symbol, timeframe);
    return chart;
  };

  public onSubscribe = (): ChartFunctions => ({
    getChart: this.getChart,
  });
}
